import React from "react";
import { Select, MenuItem, TextField, Autocomplete } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import { Text, IField, useFormContext } from "$components";
import { uniqueID } from "$utils/utils";
import { theme } from "$utils/theme/bataillog";

interface ISelect extends IField {
  errorRequired?: boolean;
  options?: any[];
}

interface InputType {
  value?: string;
  errorRegex?: boolean;
  errorRequired?: boolean;
  disabled?: boolean;
  values?: any[];
  autocomplete?: boolean;
  multiple?: boolean;
  text?: boolean;
  selection?: any;
  lists?: any[];
  setSelection?: any;
  useItemsCodes?: boolean;
}

function SelectInput({
  label,
  className,
  defaultValue = "",
  name,
  options = [],
  placeholder = "",
  value: DefValue,
  errorRequired,
  requiredText,
}: ISelect) {
  const classes = useStyles();
  const context = useFormContext();
  const select = context[name];
  const { values: formValues, setFormValues } = context;

  const [error, setError] = React.useState<string>("");

  const [inputValue, setInputValue] = React.useState<InputType>({
    value: DefValue?.value || "",
    errorRequired: errorRequired || false,
    values: DefValue?.values || [],
    autocomplete: DefValue?.autocomplete || false,
    multiple: DefValue?.multiple || false,
    text: DefValue?.text || false,
    disabled: DefValue?.disabled || false,
    useItemsCodes: DefValue?.useItemsCodes || false,
  });

  const setStateValue = (dataInput: InputType) => {
    const newValue = {
      ...inputValue,
      ...dataInput,
    };
    setInputValue(newValue);
    setFormValues({
      ...formValues,
      [name]: {
        ...(formValues[name] || {}),
        ...dataInput,
      },
    });
  };

  const OnChange = (e: any) => {
    const val = e?.target?.value || "";
    setStateValue({
      value: val,
    });
    if (select?.onSelectValue) {
      select.onSelectValue(val);
    }
  };

  const OnBlur = (e: any) => {
    const _val = e?.target?.value || "";
    if (requiredText && _val === "") {
      setError(requiredText || "");
      setStateValue({
        errorRequired: true,
      });
    } else {
      setError("");
      setStateValue({
        errorRequired: false,
      });
    }
  };

  const actionOnTextChange = (
    e: any,
    list: any[],
    setAction: (value: any) => void
  ) => {
    const _val = e?.target?.value || "";
    const res = (list || []).find((l: any) => l.name === _val);
    if (res) setAction(res);
  };

  React.useEffect(() => {
    if (errorRequired) {
      setError(requiredText || "");
    }
  }, [errorRequired, requiredText]);

  React.useEffect(() => {
    if (DefValue) {
      setInputValue((prev: any) => ({
        ...prev,
        ...DefValue,
      }));
    }
  }, [DefValue]);

  const showPlaceHolder = inputValue.value === "";
  const listOptions = DefValue?.values ? DefValue?.values : options;

  return (
    <div
      className={`${classes.inputGroup} ${
        DefValue?.disabled ? "disabled" : ""
      }`}
    >
      {label && (
        <label
          className={`${classes.inputLabel} ${
            DefValue?.disabled ? "disabled" : ""
          }`}
          htmlFor={name}
        >
          <Text>{label}</Text>
        </label>
      )}
      {DefValue?.text && !DefValue?.autocomplete && (
        <TextField
          data-placeholder-show={DefValue?.disabled ? false : showPlaceHolder}
          data-placeholder={defaultValue || ""}
          style={{}}
          name={name}
          disabled
          value={DefValue.value || ""}
          autoComplete="off"
          spellCheck="false"
          autoCapitalize="none"
          id={name}
          placeholder={placeholder}
          className={`${classes.textInput} ${className ?? ""}`}
        />
      )}

      {DefValue?.autocomplete && (
        <Autocomplete
          noOptionsText="Pas de résultat"
          className={`${classes.select} ${classes.AutoComplete}`}
          options={DefValue?.lists || []}
          disabled={DefValue?.disabled ? true : (false as boolean)}
          aria-readonly={DefValue?.disabled ? true : (false as boolean)}
          multiple={DefValue?.multiple || false}
          value={
            !DefValue?.multiple
              ? (DefValue?.lists || []).find(
                  (l: any) =>
                    l._id === DefValue?.selection?._id ||
                    l._id === DefValue?.selection
                ) || null
              : (DefValue?.lists || []).filter((l: any) =>
                  DefValue?.selection?.find(
                    (selectionItem: any) =>
                      selectionItem?._id === l._id || selectionItem === l._id
                  )
                ) || []
          }
          getOptionLabel={
            DefValue?.useItemsCodes
              ? // eslint-disable-next-line no-constant-binary-expression -- le membre gauche est un template literal donc toujours truthy, le fallback "|| option" ne s'exécute jamais ; intention originale peu claire (probablement un cas freeSolo où option est déjà une string), à vérifier avant de changer le comportement
                (option: any) => `${option?.name} - [${option?.code}]` || option
              : (option: any) => option.name
          }
          renderOption={
            DefValue?.useItemsCodes
              ? (props: any) => (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    {props.name}
                    <div
                      style={{
                        color: "grey",
                        marginLeft: 5,
                      }}
                    >{`- ${props.code}`}</div>
                  </div>
                )
              : undefined
          }
          onChange={(event: any, newValue: any) =>
            DefValue?.setSelection(newValue)
          }
          onInputChange={(e: any) =>
            actionOnTextChange(e, DefValue?.lists || [], DefValue?.setSelection)
          }
          renderInput={(params: any) => (
            <TextField
              onBlur={OnBlur}
              id={name}
              name={name}
              variant="outlined"
              placeholder={placeholder || ""}
              {...params}
            />
          )}
          data-placeholder={defaultValue || undefined}
          data-placeholder-show={DefValue?.disabled ? false : showPlaceHolder}
          id={name}
        />
      )}
      {!DefValue?.text && !DefValue?.autocomplete && (
        <Select
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            // getContentAnchorEl has been removed in MUI v5
            // Instead, anchorOrigin and transformOrigin are used to control positioning
          }}
          disabled={inputValue?.disabled || false}
          onChange={OnChange}
          onBlur={OnBlur}
          value={inputValue?.value}
          id={name}
          placeholder={placeholder}
          name={name}
          className={`${classes.selectInput} ${className || ""} ${
            showPlaceHolder ? "Placeholder" : ""
          }`}
          data-placeholder={placeholder}
          data-placeholder-show={DefValue?.disabled ? false : showPlaceHolder}
        >
          {listOptions.map((v: any) => (
            <MenuItem
              disabled={v.disabled ?? false}
              key={uniqueID()}
              value={v.value}
            >
              <Text>{v.text}</Text>
            </MenuItem>
          ))}
        </Select>
      )}
      {showPlaceHolder && !DefValue?.autocomplete && (
        <span className="PlaceHolderText">
          <Text>{placeholder}</Text>
        </span>
      )}
      <Text
        className={`${classes.errorField} ${
          showPlaceHolder ? "withPlaceholder" : "noPlaceholder"
        } ${error !== "" ? "withError" : ""}`}
      >
        {error !== "" && error}
      </Text>
    </div>
  );
}
export default React.memo(SelectInput);

const useStyles = createUseStyles(() => ({
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    marginRight: "10px",
    "& .MuiInputBase-root": {
      maxHeight: "44px",
    },
    "& .Mui-disabled": {
      backgroundColor: `${theme.color.gray2} !important`,
      color: "#000 !important",
      border: "none",
      "&:before": {
        border: "0px !important",
      },
      '& input[type="text"][name="company"]:disabled': {
        paddingLeft: "0px !important",
      },
    },
    "& .MuiAutocomplete-inputRoot": {
      minheight: "42px",
      maxHeight: "inherit",
      borderRadius: "0px",
      borderColor: "#DFE1E6",
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
      paddingLeft: "10px",
    },
    "& .MuiInputBase-root.MuiInput-root.MuiInput-underline.Mui-disabled": {
      paddingLeft: "10px",
    },
    "& svg.Mui-disabled": {
      display: "none !important",
    },
    "&.disabled .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
      {
        borderColor: "transparent !important",
      },
    "& .PlaceHolderText": {
      display: "none",
    },
    '& div[data-placeholder-show^="true"] + .PlaceHolderText': {
      display: "block",
      position: "relative",
      left: "18px",
      top: "-34px",
      pointerEvents: "none",
      "& span": {
        color: "#b7b5b5",
        pointerEvents: "none",
      },
    },
    "&.disabled .withPlaceholder": {
      top: "unset !important",
      marginBottom: "34px !important",
    },
  },
  inputLabel: {
    marginBottom: "16px",
  },
  selectInput: {
    flex: 1,
    border: `1px solid ${theme.color.gray2}`,
    paddingLeft: 10,
    paddingRight: 10,
    "&:before": {
      display: "none",
    },
    "& .MuiInput-input": {
      height: 29,
      fontSize: 15,
      display: "flex",
      alignItems: "center",
    },
  },
  AutoComplete: {
    "& + .withPlaceholder.withError": {
      marginBottom: "20px !important",
    },
    "& + .withPlaceholder": {
      top: "unset !important",
      marginBottom: "34px !important",
    },
  },
  errorField: {
    color: theme.color.red,
    fontSize: 12,
    position: "relative",
    "&.withPlaceholder": {
      top: "-20px",
      marginBottom: "8px",
      "&.withError": {
        marginBottom: "0px",
      },
    },
    "&.noPlaceholder": {
      marginBottom: "34px",
    },
  },
  textInput: {
    flex: 1,
    border: `1px solid ${theme.color.gray2}`,
    paddingLeft: 10,
    paddingRight: 10,
    "&:before": {
      display: "none",
    },
    "& input": {
      height: 29,
      fontSize: 15,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.color.gray2,
      color: "#000 !important",
    },
  },
  select: {
    flex: "1",
    "&:last-child": {
      padding: 0,
    },
    background: theme.color.white,
    "& .MuiInputLabel-shrink": {
      background: `${theme.color.white} !important`,
    },
  },
}));
